import { useEffect, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import {
  CELL_CLASSES,
  TableContainer,
  TableContent,
  TableHeader,
  TABLE_LAYOUT_OPTIONS,
  CELL_RENDERERS,
  usePreventTableScrollingRef
} from "cdk-radial";
import classNames from "classnames";

const COLUMNS = [
  {
    cellClass: CELL_CLASSES.CHECKBOX,
    checkboxSelection: true,
    field: "checkbox",
    headerClass: [CELL_CLASSES.CHECKBOX, CELL_CLASSES.SELECT_ALL],
    headerName: "",
    maxWidth: 55,
    minWidth: 55,
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: "apiName",
    headerName: "API Name",
    minWidth: 150,
    width: 250,
    tooltipField: "apiName",
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: "endpoint",
    headerName: "Endpoint",
    minWidth: 150,
    width: 250,
    tooltipField: "endpoint",
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: "version",
    headerName: "Version",
    minWidth: 75,
    width: 125,
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: "summary",
    headerName: "Summary",
    minWidth: 150,
    width: 250,
    tooltipField: "summary",
  },
];

const formatTableData = (bundleApiEndpoints, bundleApiChannels, endpointsUsedByOthers, channelsUsedByOthers, usedByContainerMap) => {
  const tableData = [];
  const endpointsData = Object.values(bundleApiEndpoints || {});
  const verbsMap = {};

  endpointsData.forEach((endpoint) => {
    const verb = endpoint.verb.toUpperCase();
    if (!verbsMap[verb]) {
      verbsMap[verb] = true;
    }
    const isDisabled = endpointsUsedByOthers.has(endpoint.endpointId);
    tableData.push({
      id: endpoint.endpointId,
      apiName: endpoint.apiName,
      endpoint: endpoint.route,
      version: `${verb} ${endpoint.version}`,
      verb: verb,
      apiType: 'api',
      summary: endpoint.summary,
      instanceId: endpoint.instanceId,
      isSelected: false,
      isDisabled: isDisabled,
      usedByContainer: isDisabled ? usedByContainerMap.get(endpoint.endpointId) : null,
    });
  });

  const channelsData = Object.values(bundleApiChannels || {});
  if (channelsData.length > 0) {
    verbsMap['CHANNELS'] = true;
  }

  channelsData.forEach((channel) => {
    const isDisabled = channelsUsedByOthers.has(channel.id);
    tableData.push({
      id: channel.id,
      apiName: channel.apiName,
      endpoint: channel.name,
      version: `${channel.version}`,
      verb: 'CHANNELS',
      apiType: 'async-api',
      summary: channel.summary,
      instanceId: channel.instanceId,
      isSelected: false,
      isDisabled: isDisabled,
      usedByContainer: isDisabled ? usedByContainerMap.get(channel.id) : null,
    });
  });

  Object.keys(verbsMap).forEach((verb) => {
    const itemsWithVerb = tableData.filter(row => row.verb === verb);
    const disabledCount = itemsWithVerb.filter(row => row.isDisabled).length;
    const totalCount = itemsWithVerb.length;

    const isChannels = verb === 'CHANNELS';
    const apiName = isChannels ? 'All Async APIs Channels' : `All APIs ${verb}s`;
    const endpoint = isChannels ? 'All Channels' : `All ${verb}s`;

    tableData.unshift({
      id: verb,
      apiName: apiName,
      endpoint: endpoint,
      version: 'N/A',
      verb: 'N/A',
      apiType: 'N/A',
      summary: 'N/A',
      instanceId: '',
      isSelected: false,
      isDisabled: disabledCount === totalCount,
      isPartiallyDisabled: disabledCount > 0 && disabledCount < totalCount,
      disabledCount: disabledCount,
      totalCount: totalCount,
    });
  });

  return tableData;
};

export default function EndpointsListTable(props) {
  const {
    // apis = [],
    ApiBundleManagementReducer,
    userSelectedChannels,
    setUserSelectedChannels,
    userSelectedEndpoints,
    setUserSelectedEndpoints,
    // isViewOnly,
  } = props;
  const {
    subPlanContainers = {},
    bundleApiEndpoints = {},
    bundleApiChannels = {},
    selectedSubPlanContainer = null,
  } = ApiBundleManagementReducer;

  const isEdit = selectedSubPlanContainer !== null;

  const [data, setData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [selectedApi, setSelectedApi] = useState(null);
  const [isRemoveError, setIsRemoveError] = useState(false);

  // Track endpoints/channels used by OTHER containers
  const [endpointsUsedByOthers, setEndpointsUsedByOthers] = useState(new Set());
  const [channelsUsedByOthers, setChannelsUsedByOthers] = useState(new Set());
  const [usedByContainerMap, setUsedByContainerMap] = useState(new Map()); // Maps endpoint/channel ID to container name

  const gridRef = useRef();
  const isSyncingGridRef = useRef(false);

  // Calculate which endpoints/channels are used by other containers
  useEffect(() => {
    const endpointIds = new Set();
    const channelIds = new Set();
    const usageMap = new Map();

    Object.entries(subPlanContainers).forEach(([containerName, container]) => {
      // Skip the container currently being edited
      if (containerName !== selectedSubPlanContainer) {
        (container.endpoints || []).forEach(ep => {
          endpointIds.add(ep.endpointId);
          usageMap.set(ep.endpointId, containerName);
        });
        (container.channels || []).forEach(ch => {
          channelIds.add(ch.id);
          usageMap.set(ch.id, containerName);
        });
      }
    });

    setEndpointsUsedByOthers(endpointIds);
    setChannelsUsedByOthers(channelIds);
    setUsedByContainerMap(usageMap);
  }, [subPlanContainers, selectedSubPlanContainer]);

  const tableContentRef = usePreventTableScrollingRef();

  useEffect(() => {
    const tableData = formatTableData(
      bundleApiEndpoints, 
      bundleApiChannels, 
      endpointsUsedByOthers, 
      channelsUsedByOthers, 
      usedByContainerMap
    );
    setData(tableData);
  }, [bundleApiEndpoints, bundleApiChannels, endpointsUsedByOthers, channelsUsedByOthers, usedByContainerMap]);

  const headerProps = {
    className: "",
    dataTestId: `table-header`,
    headerTitle: "Endpoints",
    hideBorder: false,
    hideTableHeader: false,
    hideTableTitle: false,
    id: `table-header`,
    isAlwaysExpanded: false,
    isSearchable: false,
    isPrintable: false,
    isDownloadable: false,
    overflowButtonProps: undefined,
  };

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams?.api);
    if (gridParams?.api) {
      gridParams?.api?.sizeColumnsToFit();
      gridParams?.api?.refreshCells({
        force: true,
      });
      // Selection will be handled by the useEffect hook that watches gridApi and data
    }
  };

  // Enforce grid visual state based on React state (One-way data flow)
  useEffect(() => {
    if (!gridApi || data.length === 0) return;

    // Precompute verb group selection status to avoid O(N^2) loops
    const verbGroupStatus = {};
    data.forEach(row => {
      if (row.apiType !== "N/A" && !row.isDisabled) {
        if (!verbGroupStatus[row.verb]) {
           verbGroupStatus[row.verb] = { total: 0, selected: 0 };
        }
        verbGroupStatus[row.verb].total += 1;
        const isSelected = row.apiType === 'api' 
          ? userSelectedEndpoints.includes(row.id)
          : userSelectedChannels.includes(row.id);
        if (isSelected) {
           verbGroupStatus[row.verb].selected += 1;
        }
      }
    });

    isSyncingGridRef.current = true;
    gridApi.forEachNode((node) => {
      // 1. Handle Disabled Rows
      if (node.data.isDisabled) {
        if (!node.isSelected()) {
          node.setSelected(true, false, true); // Keep visually selected, suppress events
        }
        return;
      }

      // 2. Handle Verb Groups
      if (node.data.apiType === "N/A") {
        const status = verbGroupStatus[node.data.id];
        const shouldBeSelected = status && status.total > 0 && status.selected === status.total;
        if (node.isSelected() !== shouldBeSelected) {
          node.setSelected(shouldBeSelected, false, true); // Suppress events
        }
        return;
      }

      // 3. Handle Normal Rows
      const shouldBeSelected = node.data.apiType === 'api' 
          ? userSelectedEndpoints.includes(node.data.id)
          : userSelectedChannels.includes(node.data.id);

      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected, false, true); // Suppress events
      }
    });

    // Use a small timeout to ensure any asynchronously scheduled ag-grid events are swallowed
    const timer = setTimeout(() => {
      isSyncingGridRef.current = false;
    }, 50);

    return () => clearTimeout(timer);
  }, [gridApi, data, userSelectedEndpoints, userSelectedChannels]);

  const onRowSelected = (event) => {
    if (!gridApi) return;
    
    // Ignore programmatic selections handled by our sync effect
    if (isSyncingGridRef.current) {
      return;
    }

    // Prevent any action on disabled rows (including disabled verb groups)
    if (event.data.isDisabled) {
      return;
    }

    const isApi = event.data.apiType === "api";
    const isAsyncApi = event.data.apiType === "async-api";
    const isVerbGroup = event.data.apiType === "N/A";
    const updateState = isApi ? setUserSelectedEndpoints : setUserSelectedChannels;

    if (event.node.selected) {
      if (isVerbGroup) {
        // Verb Group Selected
        const newApiIds = [];
        const newAsyncIds = [];
        data.forEach(row => {
          if (row.verb === event.data.id && row.apiType !== "N/A" && !row.isDisabled) {
            if (row.apiType === "api") newApiIds.push(row.id);
            else if (row.apiType === "async-api") newAsyncIds.push(row.id);
          }
        });
        
        if (newApiIds.length > 0) setUserSelectedEndpoints(prev => [...new Set([...prev, ...newApiIds])]);
        if (newAsyncIds.length > 0) setUserSelectedChannels(prev => [...new Set([...prev, ...newAsyncIds])]);
      } else if (isApi || isAsyncApi) {
        updateState(prev => {
           if (!prev.includes(event.data.id)) return [...prev, event.data.id];
           return prev;
        });
      }
    } else {
      if (isVerbGroup) {
        // Verb Group Deselected
        const apiIdsToRemove = new Set();
        const asyncIdsToRemove = new Set();
        data.forEach(row => {
          if (row.verb === event.data.id && row.apiType !== "N/A" && !row.isDisabled) {
            if (row.apiType === "api") apiIdsToRemove.add(row.id);
            else if (row.apiType === "async-api") asyncIdsToRemove.add(row.id);
          }
        });

        if (apiIdsToRemove.size > 0) setUserSelectedEndpoints(prev => prev.filter(id => !apiIdsToRemove.has(id)));
        if (asyncIdsToRemove.size > 0) setUserSelectedChannels(prev => prev.filter(id => !asyncIdsToRemove.has(id)));
      } else if (isApi || isAsyncApi) {
        updateState(prev => prev.filter(id => id !== event.data.id));
      }
    }
  };

  return (
    <>
      <TableContainer
        id="ag-grid-container"
        className={classNames("select-apis-table", "endpoints-list-table", "ag-theme-balham")}
      >
        <TableHeader {...headerProps} />
        <TableContent
          data-testid="table-content"
          className="select-apis-table-content"
          ref={tableContentRef}
        >
          <AgGridReact
            columnDefs={COLUMNS}
            domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
            defaultColDef={{
              sort: false,
              filter: false
            }}
            rowData={data}
            rowHeight={32}
            headerHeight={32}
            style={{ width: "100%" }}
            ref={gridRef}
            onGridReady={handleGridReady}
            enableCellTextSelection={true}
            rowSelection="multiple"
            onRowSelected={onRowSelected}
            suppressRowClickSelection={true}
            rowClassRules={{
              'ag-row-disabled': (params) => params.data.isDisabled === true,
              'ag-row-verb-disabled': (params) => params.data.apiType === 'N/A' && params.data.isDisabled === true,
            }}
          />
        </TableContent>
      </TableContainer>
    </>
  );
}
